import { hasGroupMetadata } from './quickBuyPhotoGrid';

export default (options = {}, quickCheckoutDetail) => ({
    tab: 0,
    open: false,
    loading: null,
    quickCheckoutDetail,
    filteredTeamId: null,
    refreshEvent: 'event-images::refresh',
    downloading: [],

    async fetchQuickBuyGroup(imageId) {
        try {
            const response = await fetch(`/api-quick-buy-group/${imageId}`);

            if (!response.ok) return [];

            const { data } = await response.json();

            return Array.isArray(data) ? data : [];
        } catch {
            return [];
        }
    },

    async openSliderPreview(imageId, quickBuy = false, imagePosition = null, hasClientRole = false, group = null) {
        const images = this.$store.images.all();
        const position =
            imagePosition !== null
                ? imagePosition
                : images.findIndex((image) => image.id == imageId);

        if (group === null) {
            group = quickBuy && !hasGroupMetadata(images[position])
                ? await this.fetchQuickBuyGroup(imageId)
                : [];
        }

        this.$dispatch('open::slider-preview', [
            images,
            position,
            {
                canDownload: hasClientRole,
                canFavorite: true,
                imageAttr: 'preview',
                event: this.refreshEvent,
                quickBuy,
                urlParams: options,
                hasClientRole,
                totalImagesCount: images.length,
                route: 'event',
                eventId: options.eventId,
                group,
            },
        ]);
    },

    async download({ detail: image }) {
        if (this.$isMobileSafari()) {
            this.$dispatch('view-photo-modal::open', image);

            this.downloading = this.downloading.filter(
                (downloaded) => downloaded !== _.get(image, 'name'),
            );

            this.$dispatch(this.refreshEvent, { image });

            if (this.isSaved(image)) return;

            this.$store.customerImages.toggleImage(image);

            return;
        }

        const name = _.get(image, 'name');
        const url = _.get(image, 'high_res');

        if (this.downloading.includes(name)) {
            return;
        }

        this.downloading.push(name);

        this.$downloadImage(url, name, () => {
            this.downloading = this.downloading.filter((downloaded) => downloaded !== name);

            this.$dispatch(this.refreshEvent, { image });

            if (this.isSaved(image)) return;

            this.$store.customerImages.toggleImage(image);
        });
    },

    setQuickCheckoutDetail({ detail }) {
        this.quickCheckoutDetail = detail;
    },

    async openQuickCheckout() {
        let position = this.$store.images
            .all()
            .findIndex((image) => image.id == this.quickCheckoutDetail.image.id);

        if (position === -1) {
            this.$store.images.unshift([this.quickCheckoutDetail.image]);

            position = 0;
        }

        const quickBuy = this.quickCheckoutDetail.quickBuy ?? true;
        const group = quickBuy ? (this.quickCheckoutDetail.group ?? []) : [];

        await this.openSliderPreview(this.quickCheckoutDetail.image.id, quickBuy, position, false, group);

        if (group.length > 1) {
            this.quickCheckoutDetail = {};

            return;
        }

        this.$nextTick(() => {
            if (quickBuy) {
                this.$dispatch('quick-checkout::open', {
                    package: this.quickCheckoutDetail.package,
                    imageId: this.quickCheckoutDetail.image.id,
                    urlParams: options,
                });
            }

            this.quickCheckoutDetail = {};
        });
    },

    showPreview({ detail }) {
        this.$dispatch('view-photo-tooltip::show', {
            target: detail.target,
            url: detail.data.preview,
            name: detail.data.name,
            document: this.$el,
        });
    },

    closePreview() {
        this.$dispatch('view-photo-tooltip::close');
    },

    setImages(images) {
        this.$store.images.set(images ?? []);

        this.open = true;

        if (
            !_.isEmpty(this.quickCheckoutDetail) &&
            this.quickCheckoutDetail.image.event_id === options.eventId
        ) {
            this.openQuickCheckout();
        }
    },

    async selectAll(mainEventId, eventId, teams) {
        if (
            this.$store.customerImages.openCreateAccount({ selectAll: true }) ||
            !this.canSelectAll()
        ) {
            return;
        }

        this.loading = 'saving';

        await this.fetch(
            '/save-all',
            JSON.stringify({
                main_event_id: mainEventId,
                event_id: eventId,
                teams,
            }),
        )
            .then((data) => data.json())
            .then(async () => {
                await this.$store.customerImages.updateCount();
                this.$dispatch(this.refreshEvent, { allSelected: true });
            });

        this.tab = 0;
        this.loading = null;
    },

    async deselect(mainEventId, eventId, teams) {
        this.loading = 'deleting';
        await this.fetch(
            '/remove-all',
            JSON.stringify({
                main_event_id: mainEventId,
                event_id: eventId,
                teams,
            }),
        ).then(async () => {
            this.$dispatch(this.refreshEvent, { allSelected: false });
            await this.$store.customerImages.updateCount();
        });

        this.loading = null;
        this.tab = 1;
    },

    fetch(route, params) {
        return fetch(route, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.head.querySelector('meta[name=csrf-token]').content,
            },
            body: params,
        });
    },

    saved(image) {
        return image.saved;
    },

    favorited(image) {
        return image.favorite;
    },

    save(image) {
        this.$store.customerImages.toggleImage(image);
    },

    saveImage() {
        this.$store.customerImages.saveImage();
        this.$dispatch(this.refreshEvent);
    },

    favorite(image) {
        this.$store.customerImages.toggleFavoriteImage(image);
    },

    canSelectAll() {
        return _.get(this.$store.cart.mainEvent.settings ?? {}, 'select_deselect_all', true);
    },

    confirmSaveAll(mainEventId, eventId, teams) {
        $wireui.confirmDialog({
            title: 'Are you sure?',
            description: 'This action will save all photos from that event to your album.',
            icon: 'question',
            accept: {
                label: 'Yes, select all',
                color: 'negative',
                execute: () => this.selectAll(mainEventId, eventId, teams),
            },
            reject: {
                label: 'No, cancel',
            },
        });
    },

    confirmDeselectAll(mainEventId, eventId, teams) {
        $wireui.confirmDialog({
            title: 'Are you sure?',
            description: 'This action will remove all photos from that event from your album.',
            icon: 'question',
            accept: {
                label: 'Yes, deselect all',
                color: 'negative',
                execute: () => this.deselect(mainEventId, eventId, teams),
            },
            reject: {
                label: 'No, cancel',
            },
        });
    },
});
